import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessComplaintSerialAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';

/**
 * Business → Complaint Settings.
 *
 * Org admins set the starting serial number for newly created complaints.
 * Existing tickets keep their current numbers; only the next complaint
 * (and those after it) pick up the revised series.
 */
export default function BusinessComplaintSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [startingSerial, setStartingSerial] = useState('1');

  const applyPayload = (payload) => {
    setStatus(payload);
    setStartingSerial(String(payload.starting_serial ?? 1));
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyPayload(await BusinessComplaintSerialAPI.get());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = await BusinessComplaintSerialAPI.update({
        starting_serial: startingSerial,
      });
      applyPayload(payload);
      notify.success('Complaint serial number saved.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [startingSerial]);

  if (loading && !status) return <Loader label="Loading complaint serial settings…" />;

  const nextSerial = status?.next_serial;
  const highest = status?.highest_existing_serial || 0;
  const startNum = Number(startingSerial);
  const willSkipToHighest = Number.isFinite(startNum) && startNum <= highest && highest > 0;

  return (
    <>
      <PageHeader
        title="🎫 Complaint Settings"
        subtitle="Choose the starting serial number for new complaints. Existing tickets keep their current numbers."
      />

      <ErrorBox error={error} />

      <section className="ty-card">
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>
          Serial number
        </h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0, marginBottom: 16 }}>
          New complaints are numbered in order from this starting point.
          Numbers already in use are skipped so older tickets stay as they
          are. For example, if tickets 1–12 already exist and you set the
          start to 1000, the next complaint is 1000.
        </p>

        <div className="ty-field">
          <label htmlFor="complaint-serial-start">Starting serial number</label>
          <input
            id="complaint-serial-start"
            className="ty-input"
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={startingSerial}
            onChange={(e) => setStartingSerial(e.target.value)}
          />
        </div>

        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 220px) 1fr',
            gap: '8px 16px',
            margin: '16px 0 0',
            fontSize: 14,
          }}
        >
          <dt style={{ color: 'var(--ty-muted)' }}>Highest existing serial</dt>
          <dd style={{ margin: 0 }}>{highest || '—'}</dd>
          <dt style={{ color: 'var(--ty-muted)' }}>Next complaint will be</dt>
          <dd style={{ margin: 0 }}>
            <strong>{nextSerial ?? '—'}</strong>
          </dd>
        </dl>

        {willSkipToHighest ? (
          <p style={{ fontSize: 13, color: 'var(--ty-muted)', marginBottom: 0 }}>
            That start is at or below serials already in use, so the next
            complaint will be {nextSerial} rather than duplicating an
            existing number.
          </p>
        ) : null}
      </section>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="ty-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        <button
          type="button"
          className="ty-btn ty-btn--ghost"
          onClick={reload}
          disabled={saving || loading}
        >
          Discard
        </button>
      </div>
    </>
  );
}
